import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chessboard } from 'react-chessboard';
import { Chess, Square, Piece } from 'chess.js';
import { Layout, Card, Button, Space, message, Progress, Typography, Statistic, Row, Col, Divider, Tooltip, Alert, Spin, Tabs, Select, Dropdown, Modal, Popover } from 'antd';
import type { TabsProps } from 'antd';
import { 
  ArrowLeftOutlined, 
  RedoOutlined,
  CheckOutlined,
  CloseOutlined,
  FireFilled,
  TrophyOutlined,
  AimOutlined,
  InfoCircleOutlined,
  RetweetOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { startGame, validateMove, getHint, getGameStats, resetGame } from '../services/chessService';
import MainLayout from './layout/MainLayout';
import Overlay from './Overlay'; // 新建的蒙板组件
import type { Arrow, PromotionPieceOption } from 'react-chessboard/dist/chessboard/types';
import axios from 'axios';

const { Content } = Layout;
const { Title } = Typography;

// 添加防抖函数
const debounceResize = (func: Function, wait: number = 200) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// 定义接口
interface GameState {
  sessionId: string;
  fen: string;
  isPlayerTurn: boolean;
  completed: boolean;
  stats: {
    correct_moves_count: number;
    correct_moves: number;
    wrong_moves_count: number;
    wrong_moves: number;
    current_streak: number;
    best_streak: number;
    accuracy: number;
    total_moves: number;
    completed_variations: number;
    total_variations: number;
  };
}

// 添加 Move 接口定义
interface Move {
  color: string;
  flags: string;
  piece: string;
  san: string;
  from: string;
  to: string;
  lan: string;
  before: string;
  after: string;
}

// 添加棋盘主题类型
interface BoardTheme {
  name: string;
  light: string;
  dark: string;
}

// 预设棋盘主题
const boardThemes: BoardTheme[] = [
  {
    name: '经典原木',
    light: '#EEDAB6',  // 浅木色
    dark: '#C1835B'    // 深木色
  },
  {
    name: '翡翠玉石',
    light: '#E8F3E0',  // 浅玉色
    dark: '#88AB8E'    // 深玉色
  },
  {
    name: '海岸蓝调',
    light: '#DFE9F5',  // 浅蓝灰
    dark: '#7FA1C5'    // 深蓝灰
  },
  {
    name: '摩卡咖啡',
    light: '#E6D5C3',  // 浅咖啡
    dark: '#967259'    // 深咖啡
  },
  {
    name: '紫檀雅致',
    light: '#E8E0E5',  // 浅紫灰
    dark: '#9B8B93'    // 深紫灰
  }
];

interface GameStats {
  title: string;
  total_variations: number;
  completed_variations: number;
  correct_moves: number;
  wrong_moves: number;
  total_moves: number;
  accuracy: number;
  completion_rate: number;
  current_streak: number;
  best_streak: number;
}

interface CircleStyle {
  border: string;
  borderRadius: string;
}

// 添加竖屏模式的面板组件
const PortraitPanel: React.FC<{
  gameState: GameState | null;
  stats: GameStats | null;
  onFlipBoard: () => void;
  onShowHint: () => void;
  onThemeChange: (theme: BoardTheme) => void;
  onToggleFullscreen: () => void;
  currentTheme: BoardTheme;
  activePopover: string | null;
  onPopoverVisibility: (key: string) => void;
  onShowStats: () => void;
}> = ({
  gameState,
  stats,
  onFlipBoard,
  onShowHint,
  onThemeChange,
  onToggleFullscreen,
  currentTheme,
  activePopover,
  onPopoverVisibility,
  onShowStats,
}) => (
  <Card 
    bordered={false}
    bodyStyle={{ 
      padding: '8px',
      height: 'auto',
      minHeight: 'auto',
      maxHeight: 'auto',
      background: '#fff',
      overflowX: 'hidden'
    }}
    style={{
      height: 'auto',
      background: '#fff'
    }}
  >
    <div style={{ 
      display: 'flex', 
      flexDirection: 'row', 
      gap: '8px', 
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: '0 8px'
    }}>
      <Tooltip title="翻转棋盘">
        <Button
          type="text"
          icon={<RetweetOutlined style={{ fontSize: '16px' }} />}
          onClick={onFlipBoard}
          style={{ color: '#6b46c1', padding: '4px' }}
        />
      </Tooltip>
      <Tooltip title="提示">
        <Button
          type="text"
          icon={<InfoCircleOutlined style={{ fontSize: '16px' }} />}
          onClick={onShowHint}
          style={{ color: '#d97706', padding: '4px' }}
        />
      </Tooltip>
      <Dropdown 
        menu={{
          items: boardThemes.map((theme) => ({
            key: theme.name,
            label: (
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '4px',
                overflow: 'hidden',
                display: 'flex',
                flexWrap: 'wrap',
                background: '#fff',
                padding: '2px'
              }}>
                <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
                <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
                <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
                <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
              </div>
            ),
            onClick: () => onThemeChange(theme)
          }))
        }}
        placement="topLeft"
        trigger={['click']}
      >
        <Tooltip title="换色">
          <Button
            type="text"
            icon={
              <div style={{
                width: '16px',
                height: '16px',
                borderRadius: '4px',
                overflow: 'hidden',
                display: 'flex',
                flexWrap: 'wrap',
              }}>
                <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
                <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
                <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
                <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
              </div>
            }
            style={{ color: '#2563eb', padding: '4px' }}
          />
        </Tooltip>
      </Dropdown>
      <Tooltip title="全屏">
        <Button
          type="text"
          icon={<FullscreenOutlined style={{ fontSize: '16px' }} />}
          onClick={onToggleFullscreen}
          style={{ color: '#db2777', padding: '4px' }}
        />
      </Tooltip>
      <Tooltip title="统计信息">
        <Button
          type="primary"
          icon={<MenuUnfoldOutlined style={{ 
            fontSize: '20px',
            transform: 'translateY(1px)'  // 微调图标位置
          }} />}
          onClick={onShowStats}
          style={{ 
            color: '#fff',
            padding: '6px 10px',
            height: 'auto',
            background: 'linear-gradient(120deg, #1890ff 0%, #69c0ff 100%)',
            border: 'none',
            boxShadow: '0 2px 6px rgba(24, 144, 255, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            transform: 'scale(1.1)'  // 稍微放大按钮
          }}
        />
      </Tooltip>
      <Tooltip title="走子数">
        <Popover
          content={`走子数：${gameState?.stats?.total_moves || 0}`}
          trigger="click"
          open={activePopover === 'moves'}
          onOpenChange={() => onPopoverVisibility('moves')}
          placement="top"
        >
          <Button
            type="text"
            icon={<AimOutlined style={{ fontSize: '16px' }} />}
          />
        </Popover>
      </Tooltip>
      <Tooltip title="正确率">
        <Popover
          content={`正确率：${gameState?.stats?.accuracy || 0}%`}
          trigger="click"
          open={activePopover === 'accuracy'}
          onOpenChange={() => onPopoverVisibility('accuracy')}
          placement="top"
        >
          <Button
            type="text"
            icon={<CheckOutlined style={{ fontSize: '16px' }} />}
            style={{ color: '#108ee9' }}
          />
        </Popover>
      </Tooltip>
      <Tooltip title="最佳连走">
        <Popover
          content={`最佳连走：${gameState?.stats?.best_streak || 0}`}
          trigger="click"
          open={activePopover === 'bestStreak'}
          onOpenChange={() => onPopoverVisibility('bestStreak')}
          placement="top"
        >
          <Button
            type="text"
            icon={<FireFilled style={{ fontSize: '16px' }} />}
            style={{ color: '#cf1322' }}
          />
        </Popover>
      </Tooltip>
      <Tooltip title="当前连走">
        <Popover
          content={`当前连走：${gameState?.stats?.current_streak || 0}`}
          trigger="click"
          open={activePopover === 'currentStreak'}
          onOpenChange={() => onPopoverVisibility('currentStreak')}
          placement="top"
        >
          <Button
            type="text"
            icon={<TrophyOutlined style={{ fontSize: '16px' }} />}
            style={{ 
              color: (gameState?.stats?.current_streak || 0) > 0 ? '#3f8600' : '#595959'
            }}
          />
        </Popover>
      </Tooltip>
    </div>
  </Card>
);

// 修改横屏模式的面板组件接口
const LandscapePanel: React.FC<{
  gameState: GameState | null;
  stats: GameStats | null;
  statsLoading: boolean;
  onFlipBoard: () => void;
  onShowHint: () => void;
  onThemeChange: (theme: BoardTheme) => void;
  onToggleFullscreen: () => void;
  currentTheme: BoardTheme;
}> = ({
  gameState,
  stats,
  statsLoading,
  onFlipBoard,
  onShowHint,
  onThemeChange,
  onToggleFullscreen,
  currentTheme,
}) => (
  <Card 
    bordered={false}
    bodyStyle={{ 
      padding: '0 16px 16px 16px',
      height: 'auto',
      minHeight: 'auto',
      maxHeight: 'auto',
      background: '#fff',
      overflowX: 'hidden'
    }}
    style={{
      height: 'auto',
      background: '#fff'
    }}
    title={
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '4px 0'
      }}>
        <TrophyOutlined style={{ 
          fontSize: '20px',
          color: '#059669' 
        }} />
        <span style={{
          fontSize: '16px',
          fontWeight: 500,
          color: '#1f1f1f'
        }}>{stats?.title || '未命名棋谱'}</span>
      </div>
    }
  >
    {!statsLoading ? (
      <>
        <Divider style={{ margin: '0 0 16px 0' }} />
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Button
                icon={<RetweetOutlined />}
                onClick={onFlipBoard}
                type="primary"
                style={{ 
                  width: '100%',
                  background: 'linear-gradient(120deg, #6b46c1 0%, #9f7aea 100%)',
                  border: 'none',
                  boxShadow: '0 2px 6px rgba(107, 70, 193, 0.3)'
                }}
              >
                翻转棋盘
              </Button>
            </Col>
            <Col span={12}>
              <Dropdown 
                menu={{
                  items: boardThemes.map((theme) => ({
                    key: theme.name,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px' }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexWrap: 'wrap',
                        }}>
                          <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
                          <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
                          <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
                          <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
                        </div>
                        <span>{theme.name}</span>
                      </div>
                    ),
                    onClick: () => onThemeChange(theme)
                  }))
                }}
                placement="bottom" 
                trigger={['click']}
              >
                <Button
                  type="primary"
                  style={{ 
                    width: '100%',
                    background: 'linear-gradient(120deg, #0891b2 0%, #22d3ee 100%)',
                    border: 'none',
                    boxShadow: '0 2px 6px rgba(8, 145, 178, 0.3)'
                  }}
                  icon={
                    <div style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexWrap: 'wrap',
                      marginRight: '4px'
                    }}>
                      <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
                      <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
                      <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
                      <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
                    </div>
                  }
                >
                  换色
                </Button>
              </Dropdown>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Button
                icon={<InfoCircleOutlined />}
                onClick={onShowHint}
                type="primary"
                style={{ 
                  width: '100%',
                  background: 'linear-gradient(120deg, #d97706 0%, #fbbf24 100%)',
                  border: 'none',
                  boxShadow: '0 2px 6px rgba(217, 119, 6, 0.3)'
                }}
              >
                提示
              </Button>
            </Col>
            <Col span={12}>
              <Button
                icon={<FullscreenOutlined />}
                onClick={onToggleFullscreen}
                type="primary"
                style={{ 
                  width: '100%',
                  background: 'linear-gradient(120deg, #db2777 0%, #f472b6 100%)',
                  border: 'none',
                  boxShadow: '0 2px 6px rgba(219, 39, 119, 0.3)'
                }}
              >
                全屏
              </Button>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: '24px 0' }} />

        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: '16px', fontWeight: 500 }}>完成进度</span>
            <span style={{ float: 'right', fontSize: '16px', fontWeight: 500 }}>
              {gameState?.stats?.completed_variations || 0}/{gameState?.stats?.total_variations || 0}
            </span>
          </div>
          <Progress 
            percent={Math.round(((gameState?.stats?.completed_variations || 0) / (gameState?.stats?.total_variations || 1)) * 100)} 
            status="normal"
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
            trailColor="#f0f0f0"
            strokeWidth={20}
            showInfo={false}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: '16px', fontWeight: 500 }}>正确率</span>
            <span style={{ float: 'right', fontSize: '16px', fontWeight: 500 }}>
              {gameState?.stats?.accuracy || 0}%
            </span>
          </div>
          <Progress 
            percent={gameState?.stats?.accuracy || 0}
            status="normal"
            strokeColor={{
              '0%': '#ffa39e',
              '100%': '#52c41a',
            }}
            trailColor="#f0f0f0"
            strokeWidth={20}
            showInfo={false}
          />
        </div>

        <Divider style={{ margin: '24px 0' }} />

        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic
                title="步数"
                value={gameState?.stats?.total_moves || 0}
                prefix={<AimOutlined />}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="正确率"
                value={gameState?.stats?.accuracy || 0}
                suffix="%"
                valueStyle={{ color: '#108ee9' }}
              />
            </Col>
          </Row>
        </div>

        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic
                title="正确步数"
                value={gameState?.stats?.correct_moves || 0}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckOutlined />}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="错误步数"
                value={gameState?.stats?.wrong_moves || 0}
                valueStyle={{ color: '#cf1322' }}
                prefix={<CloseOutlined />}
              />
            </Col>
          </Row>
        </div>

        <div>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic
                title="最佳连走"
                value={gameState?.stats?.best_streak || 0}
                valueStyle={{ color: '#cf1322' }}
                prefix={<FireFilled />}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="当前连走"
                value={gameState?.stats?.current_streak || 0}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: (gameState?.stats?.current_streak || 0) > 0 ? '#3f8600' : '#595959' }}
              />
            </Col>
          </Row>
        </div>
      </>
    ) : (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin />
      </div>
    )}
  </Card>
);

const ChessBoard: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game] = useState(new Chess());
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSquare, setSelectedSquare] = useState<string | null>(null);
  const [selectedPiece, setSelectedPiece] = useState<Square | null>(null);
  const [history, setHistory] = useState<Array<{
    from: string;
    to: string;
    piece: string;
    san: string;
  }>>([]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(-1);
  const [boardOrientation, setBoardOrientation] = useState<'white' | 'black'>('white');
  const [currentTheme, setCurrentTheme] = useState<BoardTheme>(boardThemes[0]);
  const [overlay, setOverlay] = useState<{
    visible: boolean;
    type: 'correct' | 'wrong' | 'complete' | null;
  }>({
    visible: false,
    type: null
  });
  const [hint, setHint] = useState<{
    from: string;
    to: string;
    visible: boolean;
  }>({
    from: '',
    to: '',
    visible: false
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [stats, setStats] = useState<GameStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const [circles, setCircles] = useState<{ [square: string]: CircleStyle }>({});
  const [legalMoves, setLegalMoves] = useState<{ [square: string]: { backgroundColor: string } }>({});
  const [promotionSourceSquare, setPromotionSourceSquare] = useState<Square | null>(null);
  const [promotionTargetSquare, setPromotionTargetSquare] = useState<Square | null>(null);
  const [showPromotionDialogForced, setShowPromotionDialogForced] = useState(false);
  const [boardKey, setBoardKey] = useState(0);

  // 在组件顶部添加音效对象和预加载处理
  // console.log(process.env.PUBLIC_URL);  // 检查 PUBLIC_URL 的值

  // 使用 useRef 来管理音频对象，确保它们在组件生命周期内保持稳定
  const audioRefs = useRef<{
    moveSound: HTMLAudioElement | null;
    wrongMoveSound: HTMLAudioElement | null;
    finishSound: HTMLAudioElement | null;
    initialized: boolean;
  }>({
    moveSound: null,
    wrongMoveSound: null,
    finishSound: null,
    initialized: false
  });

  // 初始化音频对象
  const initializeAudio = useCallback(() => {
    if (audioRefs.current.initialized) return;

    try {
      // 创建音频对象，提供多种格式支持和更好的错误处理
      const createAudioWithFallback = (baseName: string): HTMLAudioElement => {
        const audio = new Audio();
        
        // 设置音频属性
        audio.preload = 'auto';
        audio.volume = 0.7;
        
        // 尝试不同的音频格式
        const baseUrl = process.env.PUBLIC_URL || '';
        const formats = ['mp3', 'ogg', 'wav'];
        
        // 检查浏览器支持的格式
        let formatSelected = false;
        for (const format of formats) {
          const canPlay = audio.canPlayType(`audio/${format}`);
          if (canPlay === 'probably' || canPlay === 'maybe') {
            audio.src = `${baseUrl}/bg/${baseName}.${format}`;
            // console.log(`Using ${format} format for ${baseName}, support level: ${canPlay}`);
            formatSelected = true;
            break;
          }
        }
        
        // 如果没有找到明确支持的格式，默认使用 ogg（因为文件存在）
        if (!formatSelected) {
          audio.src = `${baseUrl}/bg/${baseName}.ogg`;
          console.warn(`No preferred format found for ${baseName}, using ogg as fallback`);
        }
        
        // 添加错误处理
        audio.addEventListener('error', (e) => {
          console.error(`Audio load error for ${baseName}:`, e);
          console.error(`Failed src: ${audio.src}`);
          console.error(`Audio error code: ${audio.error?.code}, message: ${audio.error?.message}`);
        });
        
        // 添加加载完成事件
        audio.addEventListener('canplaythrough', () => {
          console.log(`Audio ${baseName} loaded and ready to play`);
        });
        
        // 添加加载开始事件
        audio.addEventListener('loadstart', () => {
          console.log(`Started loading audio ${baseName}`);
        });
        
        return audio;
      };

      // 创建所有音频对象
      const moveSound = createAudioWithFallback('move');
      const wrongMoveSound = createAudioWithFallback('wrongmove');
      const finishSound = createAudioWithFallback('finishline');

      // 预加载音频
      [moveSound, wrongMoveSound, finishSound].forEach((audio, index) => {
        const names = ['move', 'wrongmove', 'finishline'];
        // console.log(`Loading audio: ${names[index]}, src: ${audio.src}`);
        audio.load();
      });

      // 保存到 ref
      audioRefs.current = {
        moveSound,
        wrongMoveSound,
        finishSound,
        initialized: true
      };

      // console.log('Audio initialized successfully');
    } catch (error) {
      console.error('Failed to initialize audio:', error);
      // 即使音频初始化失败，也标记为已初始化，避免重复尝试
      audioRefs.current.initialized = true;
    }
  }, []);

  // 播放音效函数
  const playSound = useCallback((soundType: 'move' | 'wrong' | 'finish') => {
    try {
      if (!audioRefs.current.initialized) {
        console.warn('Audio not initialized yet');
        return;
      }

      let audio: HTMLAudioElement | null = null;
      
      switch (soundType) {
        case 'move':
          audio = audioRefs.current.moveSound;
          break;
        case 'wrong':
          audio = audioRefs.current.wrongMoveSound;
          break;
        case 'finish':
          audio = audioRefs.current.finishSound;
          break;
      }

      if (!audio) {
        console.warn(`Audio object for ${soundType} not found`);
        return;
      }

      // 检查音频是否可以播放
      // console.log(`Playing ${soundType} sound, readyState: ${audio.readyState}, src: ${audio.src}`);
      
      if (audio.readyState < 2) { // HAVE_CURRENT_DATA
        console.warn(`Audio ${soundType} not ready, current state: ${audio.readyState}`);
        // 尝试重新加载
        audio.load();
        
        // 监听加载完成后播放
        const onCanPlay = () => {
          if (!audio) return; // 添加null检查
          audio.removeEventListener('canplay', onCanPlay);
          audio.currentTime = 0;
          audio.play().catch(error => {
            console.error(`Error playing ${soundType} after reload:`, error);
          });
        };
        audio.addEventListener('canplay', onCanPlay);
        return;
      }

      // 重置音频到开始位置
      audio.currentTime = 0;
      
      // 播放音频
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise.then(() => {
          // console.log(`Successfully played ${soundType} sound`);
          console.log("成功播放提示音")
        }).catch(error => {
          // console.error(`Error playing ${soundType} sound:`, error);
          // console.error(`Audio src: ${audio?.src}`);
          // console.error(`Audio readyState: ${audio?.readyState}`);
          // console.error(`Audio error:`, audio?.error);
          
          // 根据错误类型提供不同的处理方案
          if (error.name === 'NotAllowedError') {
            console.warn('Audio play was prevented by browser policy. User interaction may be required.');
          } else if (error.name === 'NotSupportedError') {
            console.error(`Audio format not supported for ${soundType}. Trying to reload with different approach.`);
            // 尝试重新加载音频
            if (audio) {
              audio.load();
            }
          } else if (error.name === 'AbortError') {
            console.warn(`Audio play was aborted for ${soundType}`);
          }
        });
      }
    } catch (error) {
      console.error(`Error in playSound for ${soundType}:`, error);
    }
  }, []);

  // 在组件挂载时初始化音频
  useEffect(() => {
    initializeAudio();
    
    // 清理函数
    return () => {
      if (audioRefs.current.initialized) {
        [audioRefs.current.moveSound, audioRefs.current.wrongMoveSound, audioRefs.current.finishSound].forEach(audio => {
          if (audio) {
            audio.pause();
            audio.src = '';
          }
        });
        audioRefs.current = {
          moveSound: null,
          wrongMoveSound: null,
          finishSound: null,
          initialized: false
        };
      }
    };
  }, [initializeAudio]);

  // 添加用户首次交互监听器来启用音频（浏览器安全策略要求）
  useEffect(() => {
    const enableAudio = () => {
      if (!audioRefs.current.initialized) {
        initializeAudio();
      }
      // 移除监听器，只需要执行一次
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };

    document.addEventListener('click', enableAudio);
    document.addEventListener('keydown', enableAudio);

    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };
  }, [initializeAudio]);

  // 在组件内hint 状态定义之后
  const customArrows: Arrow[] = hint.visible ? [
    [
      hint.from as Square,
      hint.to as Square,
      'rgb(255, 215, 0, 0.8)'  // 半透明黄色
    ]
  ] : [];

  // 初始化游戏
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const colorParam = searchParams.get('color');
    const selectedColor = (colorParam === 'black' || colorParam === 'white') ? colorParam : 'white';
    setBoardOrientation(selectedColor);
    
    if (id) {
      initializeGame(selectedColor);
    }
  }, [id]); // 添加 id 作为依赖

  const initializeGame = async (selectedColor: 'white' | 'black' = 'white') => {
    try {
      setLoading(true);
      
      // 立即清除所有UI状态 - 放在最开始
      setSelectedPiece(null);
      setLegalMoves({});
      setPromotionSourceSquare(null);
      setPromotionTargetSquare(null);
      setShowPromotionDialogForced(false);
      setBoardKey(prev => prev + 1);
      setCircles({});
      setHint({ from: '', to: '', visible: false });
      
      // 立即清除DOM中的标记
      document.querySelectorAll('.with-circle').forEach(element => {
        element.classList.remove('with-circle');
      });
      
      game.reset(); // 重置棋盘状态
      const response = await startGame(Number(id), selectedColor);
      
      // console.log('Start game response:', response);
      
      // 如果有nextFen，直接设置初始局面
      if (response.nextFen) {
        game.load(response.nextFen);
      }
      
      // 设置游戏状态，保留后端返回的统计数据
      const isPlayerTurn = (selectedColor === 'white' && game.turn() === 'w') || 
                          (selectedColor === 'black' && game.turn() === 'b');
      
      setGameState({
        sessionId: response.sessionId,
        fen: game.fen(),
        isPlayerTurn,
        completed: response.completed || false,
        stats: response.stats || {
          correct_moves_count: 0,
          correct_moves: 0,
          wrong_moves_count: 0,
          wrong_moves: 0,
          current_streak: 0,
          best_streak: 0,
          accuracy: 0,
          total_moves: 0,
          completed_variations: 0,
          total_variations: 0,
        }
      });

      // 如果游戏已完成，显示完成蒙版
      if (response.completed) {
        setOverlay({ visible: true, type: 'complete' });
      }

      // 立即加载完整的统计数据
      await loadStats();
      
    } catch (error) {
      if (error instanceof Error && error.message.includes('session')) {
        message.error('会话已过期，请重新登录');
        navigate('/login');
      } else {
        // console.error('Initialize game error:', error);
        message.error('初始化游戏失败');
        navigate('/games');
      }
    } finally {
      setLoading(false);
    }
  };

  // 辅助函数：将数字索引转换为棋盘坐标
  const indexToSquare = (index: number): string => {
    const file = String.fromCharCode('a'.charCodeAt(0) + (index % 8));
    const rank = Math.floor(index / 8) + 1;
    return `${file}${rank}`;
  };

  // 修改重置开始游戏函数
  const handleRestart = async () => {
    try {
      // 立即清除所有UI状态 - 放在最开始
      setSelectedPiece(null);
      setLegalMoves({});
      setPromotionSourceSquare(null);
      setPromotionTargetSquare(null);
      setShowPromotionDialogForced(false);
      setBoardKey(prev => prev + 1);
      setCircles({});
      setHint({ from: '', to: '', visible: false });
      
      // 立即清除DOM中的标记
      document.querySelectorAll('.with-circle').forEach(element => {
        element.classList.remove('with-circle');
      });

      // 使用setTimeout确保状态更新完成
      await new Promise(resolve => setTimeout(resolve, 0));

      if (!gameState?.sessionId) {
        message.error('会话已过期，请重新登录');
        navigate('/login');
        return;
      }

      if (!id) {
        message.error('游戏ID无效');
        return;
      }

      // Store the current color from board orientation
      const currentColor = boardOrientation as 'white' | 'black';

      // 调用重置游戏API
      await resetGame(gameState.sessionId, parseInt(id));
      
      // 重置棋盘
      game.reset();
      
      // 重新初始化游戏，保持当前颜色
      await initializeGame(currentColor);
      
      // 关闭蒙版
      setOverlay({ visible: false, type: null });
      
      message.success('游戏已重置');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        // 显示后台返回的具体错误信息
        message.error(error.response.data.error);
      } else if (error instanceof Error && error.message.includes('session')) {
        message.error('会话已过期，请重新登录');
        navigate('/login');
      } else {
        // 如果没有具体错误信息，显示通用错误
        message.error(error instanceof Error ? error.message : '重置游戏失败');
      }
    }
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'r' || event.key === 'R') {
        handleRestart();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // 处理棋子点击
  const onSquareClick = (square: Square) => {
    // 先清除所有红圈
    clearAllMarkers();

    if (!gameState?.isPlayerTurn) return;
    
    // 如果之前没有选中棋子
    if (!selectedPiece) {
      const piece = game.get(square);
      // 检查是否是当前回合的棋子
      const isCorrectTurn = piece?.color === game.turn();
      
      if (piece && isCorrectTurn) {
        setSelectedPiece(square);
        // 显示可能的移动位置
        const moves = game.moves({ 
          square: square,
          verbose: true 
        });

        const newLegalMoves: { [square: string]: { backgroundColor: string } } = {};
        moves.forEach((move: any) => {
          newLegalMoves[move.to] = { 
            backgroundColor: 'rgba(255, 77, 79, 0.3)'
          };
        });
        setLegalMoves(newLegalMoves);
      }
    } 
    // 如果已选中了棋子
    else {
      // 如果点击同一个格子，取消选择
      if (square === selectedPiece) {
        setSelectedPiece(null);
        setLegalMoves({});
        return;
      }

      // 检查移动是否合法
      const moves = game.moves({ verbose: true });
      
      // 检查是否是升变移动
      const sourcePiece = game.get(selectedPiece);
      const isPromotion = sourcePiece?.type === 'p' && (square.charAt(1) === '8' || square.charAt(1) === '1');
      
      // console.log('=== onSquareClick move validation ===');
      // console.log('From:', selectedPiece, 'To:', square);
      // console.log('Source piece:', sourcePiece);
      // console.log('Is promotion move:', isPromotion);
      
      // 统一检查：只要存在从selectedPiece到square的移动就是合法的
      const isValidMove = moves.some(move => 
        move.from === selectedPiece && 
        move.to === square
      );
      
      // console.log('Is valid move:', isValidMove);

      if (!isValidMove) {
        // console.log('Move rejected as invalid');
        message.error('不符合国际象棋规则的移动');
      setSelectedPiece(null);
      setLegalMoves({});
        return;
      }

      if (isPromotion) {
        // 设置升变状态，触发升变对话框
        setPromotionSourceSquare(selectedPiece);
        setPromotionTargetSquare(square);
        setShowPromotionDialogForced(true);
        
        // 清除选择状态
        setSelectedPiece(null);
        setLegalMoves({});
        
        return; // 等待用户选择升变棋子
      }

      // 非升变的普通移动，调用onDrop处理
      const piece = game.get(selectedPiece);
      const result = onDrop(selectedPiece, square, piece ? `${piece.color}${piece.type}` : '');
      // console.log('onDrop result:', result);
    }
  };

  // 修改右键点击处理函数
  const handleContextMenu = (event: React.MouseEvent) => {
    // 只在按住 Shift 键时处理画圈
    if (!event.shiftKey) return;

    // 不阻止事件冒泡，让 react-chessboard 也能处理右键事件
    event.preventDefault();

    // 获取点击的格子位置
    const element = event.target as HTMLElement;
    // 向上查找直到找到带有 data-square 属性的元素
    const squareElement = element.closest('[data-square]');
    const square = squareElement?.getAttribute('data-square');
    
    if (square && squareElement) {
      setCircles(prev => {
        const newCircles = { ...prev };
        if (newCircles[square]) {
          // 如果该格子已有红圈，则移除
          delete newCircles[square];
          squareElement.classList.remove('with-circle');
        } else {
          // 添加新的红圈
          newCircles[square] = {
            border: '3px solid rgba(255, 0, 0, 0.7)',
            borderRadius: '50%'
          };
          squareElement.classList.add('with-circle');
        }
        return newCircles;
      });
    }
  };

  // 修改清除所有标记的函数
  const clearAllMarkers = () => {
    // 移除所有红圈类
    document.querySelectorAll('.with-circle').forEach(element => {
      element.classList.remove('with-circle');
    });
    setCircles({});
  };

  // 修改棋盘样式
  const customSquareStyles = {
    ...(hint.visible && {
      [hint.from]: {
        backgroundColor: 'rgba(255, 215, 0, 0.3)'
      },
      [hint.to]: {
        backgroundColor: 'rgba(255, 215, 0, 0.3)'
      }
    }),
    ...legalMoves,
    ...(selectedSquare && { [selectedSquare]: { backgroundColor: 'rgba(255, 255, 0, 0.4)' } }),
    ...circles
  };

  // 在拖动开始时清除点击选择
  const onPieceDragBegin = (piece: string, sourceSquare: string) => {
    // 清除所有红圈
    clearAllMarkers();
    
    setSelectedPiece(null);
    if (!gameState?.isPlayerTurn) return;

    try {
      const moves = game.moves({
        square: sourceSquare as Square,
        verbose: true 
      });

      const newLegalMoves: { [square: string]: { backgroundColor: string } } = {};
      moves.forEach((move: any) => {
        newLegalMoves[move.to] = { 
          backgroundColor: 'rgba(255, 77, 79, 0.3)'
        };
      });

      setLegalMoves(newLegalMoves);
    } catch (error) {
      console.error('Error calculating legal moves:', error);
    }
  };

  // 在拖动结束时清除所有高亮
  const onPieceDragEnd = () => {
    setLegalMoves({});
  };

  // 处理棋谱点击
  const handleMoveClick = (index: number) => {
    game.reset();
    // 重放到选中的步骤
    for (let i = 0; i <= index; i++) {
      game.move(history[i].san);
    }
    setCurrentMoveIndex(index);
    setGameState(prev => ({
      ...prev!,
      fen: game.fen()
    }));
  };

  // 记录移动历
  const recordMove = (move: any) => {
    setHistory(prev => [...prev, {
      from: move.from,
      to: move.to,
      piece: move.piece,
      san: move.san
    }]);
  };

  // 添加翻转棋盘函数
  const flipBoard = () => {
    setBoardOrientation(current => current === 'white' ? 'black' : 'white');
  };

  // 修改主题选择器为下拉按钮
  const ThemeSelector = () => {
    const menu = {
      items: boardThemes.map((theme) => ({
        key: theme.name,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              overflow: 'hidden',
              display: 'flex',
              flexWrap: 'wrap',
            }}>
              <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
              <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
              <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
              <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
            </div>
            <span>{theme.name}</span>
          </div>
        ),
        onClick: () => {
          setCurrentTheme(theme);
          setGameState(prev => ({
            ...prev!,
            fen: game.fen()
          }));
        }
      }))
    };

    return (
      <Dropdown menu={menu} placement="bottom" trigger={['click']}>
        <Button
          type="default"
          style={{ width: '100%' }}
          icon={
            <div style={{
              width: '14px',
              height: '14px',
              borderRadius: '3px',
              overflow: 'hidden',
              display: 'flex',
              flexWrap: 'wrap',
              marginRight: '4px'
            }}>
              <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
              <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
              <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
              <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
            </div>
          }
        >
          换色
        </Button>
      </Dropdown>
    );
  };

  // 修改控制栏组件
  const BoardControls = () => (
    <div className={isFullscreen ? 'fullscreen-controls' : undefined}>
      <div style={{ 
        padding: '12px 24px',
        borderBottom: '1px solid #f0f0f0',
        display: isFullscreen ? 'none' : 'flex',  // 全屏时隐藏标题
        alignItems: 'center',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '4px',
            height: '24px',
            background: 'linear-gradient(to bottom, #1890ff, #52c41a)',
            borderRadius: '2px',
            
          }} />
          <Typography.Title 
            level={4} 
            style={{ 
              margin: 0,
              color: '#1f1f1f',
              fontSize: '20px',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}
          >
            {stats?.title || '未命名棋谱'}
          </Typography.Title>
        </div>

        <Space size="small">
          <ThemeSelector />
          <Button
            icon={<RetweetOutlined />}
            onClick={flipBoard}
            type="text"
            size="middle"
          >
            翻转棋盘
          </Button>
          <Tooltip title="显示下一步提示">
            <Button
              icon={<InfoCircleOutlined />}
              onClick={showHint}
              type="text"
              size="middle"
            >
              提示
            </Button>
          </Tooltip>
          <Button
            icon={<ClearOutlined />}
            onClick={clearAllMarkers}
            type="text"
            size="middle"
            style={{ color: '#ff4d4f' }}
          >
            清除标记
          </Button>
          <Tooltip title="按住 Shift + 右键点击棋盘格子可以画红圈">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
          <Tooltip title={isFullscreen ? "退出全屏" : "全屏模式"}>
            <Button
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
              type="text"
              size="middle"
            >
              {isFullscreen ? "退出全屏" : "全屏"}
            </Button>
          </Tooltip>
        </Space>
      </div>
    </div>
  );

  const showHint = async () => {
    try {
      if (!gameState?.sessionId) {
        message.error('会话已过期，请重新登录');
        navigate('/login');
        return;
      }

      const response = await getHint(gameState.sessionId);
      
      if (response.success) {
        setHint({
          from: response.from,
          to: response.to,
          visible: true
        });
        
        setTimeout(() => {
          setHint(prev => ({ ...prev, visible: false }));
        }, 3000);
      } else {
        if (response.message?.includes('session')) {
          message.error('会话已过期，请重新登录');
          navigate('/login');
        } else {
          message.info(response.message || '当前没有可用的提示');
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('session')) {
        message.error('会话已过期，请重新登录');
        navigate('/login');
      } else {
        message.error('获取提示失败');
      }
    }
  };

  // 添加防抖函数
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // 修改屏幕尺寸监听
  useEffect(() => {
    const handleResize = debounceResize(() => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      const newIsPortrait = currentHeight > currentWidth;
      
      setIsMobile(currentWidth <= 768);
      setIsTablet(currentWidth <= 1024 && currentWidth > 768);
      setIsPortrait(newIsPortrait);
    });

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 监听方向变化，更新折叠状态
  useEffect(() => {
    if (isPortrait) {
      setCollapsed(true);
    }
  }, [isPortrait]);

  // 修改 loadStats 函数，使用 useCallback 包装
  const loadStats = useCallback(async () => {
    if (!id) return;
    try {
      setStatsLoading(true);
      const statsData = await getGameStats(parseInt(id));
      if (statsData) {
        // ('Loaded stats:', statsData); // 添加日志
        setStats(statsData);
        // 更新 gameState 中的统计数据
        setGameState(prev => {
          if (!prev) return null;
          return {
            ...prev,
            stats: {
              ...prev.stats,
              ...statsData
            }
          };
        });
      }
    } catch (error) {
      console.error('Load stats error:', error);
      message.error('加载统计数据失败');
    } finally {
      setStatsLoading(false);
    }
  }, [id]);

  // 创建防抖本的 loadStats
  const debouncedLoadStats = useCallback(
    debounce(() => loadStats(), 1000),
    [loadStats]
  );

  // 修改统计数据加载的时机
  useEffect(() => {
    if (id) {
      // 只在初始化时加载一次完整统计信息
      loadStats();
    }
  }, [id]); // 移除 gameState?.stats?.total_moves 依赖

  // 在组件内添加样式
  const styles = `
    .chess-board-container {
      width: 100%;
    }
    
    .chess-board-container .board-container {
      position: relative;
      padding: 22px;
      background: linear-gradient(145deg, #f0f0f0, #e6e6e6);
      border-radius: 8px;
      width: 100%;
      box-shadow: 
        8px 8px 16px rgba(174, 174, 174, 0.2),
        -8px -8px 16px rgba(255, 255, 255, 0.7),
        inset 1px 1px 2px rgba(255, 255, 255, 0.3),
        inset -1px -1px 2px rgba(174, 174, 174, 0.1);
    }

    .chess-board-container .coordinate-wrapper {
      position: relative;
      width: 100%;
      aspect-ratio: 1;
    }
    
    .chess-board-container .board-coordinates {
      position: absolute;
      display: flex;
      justify-content: space-around;
      align-items: center;
      color: rgba(90, 90, 90, 0.75);
      font-family: "Segoe UI", Arial, sans-serif;
      font-size: 15px;
      font-weight: 500;
      z-index: 2;
      letter-spacing: 0.5px;
      pointer-events: none;
    }
    
    .chess-board-container .rank-coordinates {
      top: 0;
      left: -20px;
      height: 100%;
      flex-direction: column;
    }
    
    .chess-board-container .file-coordinates {
      bottom: -20px;
      left: 0;
      width: 100%;
      flex-direction: row;
    }
    
    .chess-board-container .coordinate-label {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      text-shadow: 
        1px 1px 1px rgba(255, 255, 255, 0.8),
        -1px -1px 1px rgba(174, 174, 174, 0.2);
    }

    .chess-board-container .react-chessboard {
      width: 100% !important;
      height: 100% !important;
      box-shadow: 
        2px 2px 4px rgba(0, 0, 0, 0.1),
        -1px -1px 3px rgba(255, 255, 255, 0.7);
      border-radius: 4px;
      overflow: hidden;
    }

    /* 只给有红圈的格子添加样式 */
    .chess-board-container .with-circle {
      position: relative;
    }

    .chess-board-container .with-circle::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      right: 2px;
      bottom: 2px;
      border: 3px solid rgba(255, 0, 0, 0.7);
      border-radius: 50%;
      pointer-events: none;
    }
    
    .chess-board-container:fullscreen {
      display: flex;
      flex-direction: column;
      background: #000000;
      padding: 0;
      position: relative;
    }
    
    .chess-board-container:fullscreen .ant-card-body {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0 !important;
      background: #000000;
    }
    
    .chess-board-container:fullscreen .chess-board-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 24px;
      padding: 24px;
      position: relative;
      max-width: 100vh;
      margin: 0 auto;
      width: 100%;
    }

    .chess-board-container:fullscreen .board-container {
      width: 100%;
      max-width: min(100vh - 200px, 100vw - 48px);
      aspect-ratio: 1;
    }

    .fullscreen-controls {
      display: flex;
      flex-direction: row;
      gap: 12px;
      padding: 12px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      backdrop-filter: blur(4px);
      z-index: 1000;
      margin-top: 12px;
      justify-content: center;
      width: 100%;
      max-width: min(100vh - 200px, 100vw - 48px);
    }

    .fullscreen-controls .ant-btn {
      width: 40px;
      height: 40px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.1);
      border: none;
      color: #ffffff;
      font-size: 20px;
    }

    .fullscreen-controls .ant-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      background: rgba(255, 255, 255, 0.2);
    }

    @media (orientation: landscape) {
      .chess-board-container:fullscreen .chess-board-wrapper {
        flex-direction: row;
      }
      
      .fullscreen-controls {
        flex-direction: column;
        margin-top: 0;
        margin-right: 24px;
        width: auto;
      }
    }
  `;

  // 在组件添加样式入
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      const chessboardContainer = document.querySelector('.chess-board-container');
      if (chessboardContainer) {
        chessboardContainer.requestFullscreen().catch(err => {
          message.error('无法进入全屏模式');
        });
      }
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // 添加处理下一关的函数
  const handleNextLevel = async () => {
    try {
      // 立即清除所有UI状态 - 放在最开始
      setSelectedPiece(null);
      setLegalMoves({});
      setPromotionSourceSquare(null);
      setPromotionTargetSquare(null);
      setShowPromotionDialogForced(false);
      setBoardKey(prev => prev + 1);
      setCircles({});
      setHint({ from: '', to: '', visible: false });
      
      // 立即清除DOM中的标记
      document.querySelectorAll('.with-circle').forEach(element => {
        element.classList.remove('with-circle');
      });

      // 使用setTimeout确保状态更新完成
      await new Promise(resolve => setTimeout(resolve, 0));

      // Store the current color from board orientation
      const currentColor = boardOrientation;
      
      // 重置棋盘
      game.reset();
      
      // 重新初始化游戏，保持当前颜色
      await initializeGame(currentColor);
      
      // 关闭蒙版
      setOverlay({ visible: false, type: null });
      
      message.success('开始下一关');
    } catch (error) {
      if (error instanceof Error && error.message.includes('session')) {
        message.error('会话已过期，请重新登录');
        navigate('/login');
      } else {
        message.error('加载下一关失败');
      }
    }
  };

  const isCompleted = 
    gameState?.completed || 
    (gameState?.stats?.total_variations !== undefined && 
     gameState?.stats?.completed_variations !== undefined &&
     gameState.stats.total_variations > 0 &&
     gameState.stats.completed_variations === gameState.stats.total_variations);

  // Add new function to handle popover visibility
  const handlePopoverVisibility = (key: string) => {
    setActivePopover(activePopover === key ? null : key);
  };

  // 在组件卸载时清理标记
  useEffect(() => {
    return () => {
      // 移除所有红圈类
      document.querySelectorAll('.with-circle').forEach(element => {
        element.classList.remove('with-circle');
      });
      setCircles({});
    };
  }, []);

  // 在 Overlay 组件中，修改返回按钮的行为
  const handleReturn = () => {
    // 无需添加参数，直接返回到 /games 路径
    // localStorage 中已保存了所需的分类和分页信息
    navigate('/games');
  };

  // 修改处理棋子移动的函数
  const onDrop = (sourceSquare: Square, targetSquare: Square, piece: string): boolean => {
    if (!gameState?.isPlayerTurn) return false;

    // 检查是否是玩家的回合
    const pieceColor = piece.charAt(0); // 'w' for white, 'b' for black
    const currentTurn = game.turn(); // 'w' for white, 'b' for black

    // 确保玩家只能在自己的回合移动棋子
    if (currentTurn !== pieceColor) {
      message.error('现在不是你的回合');
      // 清除选择状态
      setSelectedPiece(null);
      setLegalMoves({});
      return false;
    }

    // 检查移动是否合法
    const moves = game.moves({ verbose: true });
    const isValidMove = moves.some(move => 
      move.from === sourceSquare && 
      move.to === targetSquare
    );

    if (!isValidMove) {
      message.error('不符合国际象棋规则的移动');
      // 清除选择状态
      setSelectedPiece(null);
      setLegalMoves({});
      return false;
    }

    // 检查是否是升变移动
    const sourcePiece = game.get(sourceSquare);
    const isPromotion = sourcePiece?.type === 'p' && (targetSquare.charAt(1) === '8' || targetSquare.charAt(1) === '1');
    
    if (isPromotion) {
      // 手动处理升变：设置升变状态并显示自定义对话框
      setPromotionSourceSquare(sourceSquare);
      setPromotionTargetSquare(targetSquare);
      setShowPromotionDialogForced(true);
      return false; // 不让棋子移动，等待用户选择升变棋子
    }

    // 保存当前FEN，以便在校验失败时恢复
    const currentFen = game.fen();
    
    // 尝试在本地执行移动（非升变移动）
    const move = game.move({
      from: sourceSquare,
      to: targetSquare
    });

    // 如果移动在本地是有效的
    if (move) {
      // 构造UCI格式的移动字符串
      const moveUci = `${sourceSquare}${targetSquare}`;

      // 清除选择状态（非升变的普通移动）
      setSelectedPiece(null);
      setLegalMoves({});

      // 异步验证移动
      validateMoveAsync(moveUci, sourceSquare, targetSquare, currentFen);
      return true;
    }

    // 移动失败，清除选择状态
    setSelectedPiece(null);
    setLegalMoves({});
    return false;
  };

  // 添加通用的移动验证函数
  const validateMoveAsync = async (
    moveUci: string,
    sourceSquare: Square,
    targetSquare: Square,
    currentFen: string
  ) => {
    try {
      if (!gameState?.sessionId) return;
      
      // 先临时更新棋盘显示，但保留FEN以便在验证失败时恢复
      setGameState(prev => ({
        ...prev!,
        fen: game.fen()
      }));
      
      const response = await validateMove(gameState.sessionId, moveUci);
      
      if (response.valid) {
        playSound('move');
        
        // 更新棋盘状态
        setGameState(prev => prev ? {
          ...prev,
          fen: game.fen(),
          stats: {
            ...prev.stats,
            ...response.stats
          }
        } : prev);
        
        // 显示正确提示
        setOverlay({ visible: true, type: 'correct' });
        setTimeout(() => {
          setOverlay({ visible: false, type: null });
          
          if (response.isLastMove) {
            playSound('finish');
            setOverlay({ visible: true, type: 'complete' });
            if (typeof debouncedLoadStats === 'function') {
              debouncedLoadStats();
            }
          }
        }, 1000);
        
        // 处理对方的移动
        if (response.nextMove) {
          setTimeout(() => {
            try {
              // 确保棋盘状态正确
              if (response.nextFen) {
                game.load(response.nextFen);
              }
              
              // 执行对手的移动
              const opponentMove = game.move({
                from: response.nextMove.from,
                to: response.nextMove.to,
                promotion: response.nextMove.promotion || 'q'
              });
              
              setGameState(prev => {
                if (!prev) return null;
                return {
                  ...prev,
                  isPlayerTurn: true,
                  fen: game.fen()
                };
              });
            } catch (moveError) {
              console.error('Error making opponent move:', moveError);
              // 如果出现错误，使用服务器返回的 FEN
              if (response.nextFen) {
                game.load(response.nextFen);
                setGameState(prev => ({
                  ...prev!,
                  isPlayerTurn: true,
                  fen: response.nextFen
                }));
              }
            }
          }, 500);
        }
      } else {
        // 验证失败，恢复棋盘状态
        game.load(currentFen);
        setGameState(prev => ({
          ...prev!,
          fen: currentFen
        }));
        
        playSound('wrong');
        
        // 更新统计数据
        setGameState(prev => ({
          ...prev!,
          stats: {
            ...prev!.stats,
            ...response.stats
          }
        }));
        
        // 显示错误提示
        setOverlay({ visible: true, type: 'wrong' });
        
        setTimeout(() => {
          setOverlay({ visible: false, type: null });
        }, 1000);
      }
    } catch (error) {
      // 处理错误，恢复棋盘状态
      game.load(currentFen);
      setGameState(prev => ({
        ...prev!,
        fen: currentFen
      }));
      
      if (error instanceof Error && error.message.includes('session')) {
        message.error('会话已过期，请重新登录');
        navigate('/login');
      } else {
        message.error('验证移动失败');
      }
    }
  };

  // 添加处理升变选择的函数
  const handlePromotionPieceSelect = (
    promotionPiece: PromotionPieceOption | undefined,
    fromSquare: Square | undefined,
    toSquare: Square | undefined
  ): boolean => {
    // 如果没有提供所需参数，尝试使用保存的状态
    if (!promotionPiece) {
      setPromotionSourceSquare(null);
      setPromotionTargetSquare(null);
      setShowPromotionDialogForced(false);
      return false;
    }

    // 使用传入的参数，如果没有则使用保存的状态作为后备
    const effectiveFromSquare = fromSquare || promotionSourceSquare;
    const effectiveToSquare = toSquare || promotionTargetSquare;

    if (!effectiveFromSquare || !effectiveToSquare) {
      setPromotionSourceSquare(null);
      setPromotionTargetSquare(null);
      setShowPromotionDialogForced(false);
      return false;
    }

    try {
      // 从promotionPiece中提取升变棋子类型 (如'wQ'中的'Q'代表皇后)
      // 将其转换为小写以匹配chess.js的要求: 'q'=皇后, 'r'=车, 'n'=马, 'b'=象
      const promotion = promotionPiece.charAt(1).toLowerCase();
      
      // 构造UCI格式的移动字符串，包含升变信息
      const moveUci = `${effectiveFromSquare}${effectiveToSquare}${promotion}`;

      // 保存当前FEN，以便在校验失败时恢复
      const currentFen = game.fen();
      
      // 立即重置升变状态，关闭对话框
      setPromotionSourceSquare(null);
      setPromotionTargetSquare(null);
      setShowPromotionDialogForced(false);
      
      // 使用升变移动验证函数
      validatePromotionMove(moveUci, effectiveFromSquare, effectiveToSquare, promotion, currentFen);
      
      return true;
    } catch (error) {
      console.error('处理升变时发生错误:', error);
      // 移动失败或发生错误，重置升变状态
      setPromotionSourceSquare(null);
      setPromotionTargetSquare(null);
      setShowPromotionDialogForced(false);
      return false;
    }
  };
  
  // 修改处理升变移动验证的函数
  const validatePromotionMove = async (
    moveUci: string, 
    fromSquare: Square, 
    toSquare: Square, 
    promotion: string,
    currentFen: string
  ) => {
    try {
      if (!gameState?.sessionId) return;
      
      // 检查移动是否在当前棋盘状态下合法
      const moves = game.moves({ verbose: true });
      const promotionMoves = moves.filter(move => 
        move.from === fromSquare && 
        move.to === toSquare &&
        move.flags.includes('p')
      );
      
      if (promotionMoves.length === 0) {
        console.error('No promotion moves available for this position!');
        message.error('不符合国际象棋规则的移动');
        return;
      }
      
      // 向后端发送校验请求，但不在本地执行移动
      const response = await validateMove(gameState.sessionId, moveUci);
      
      if (response.valid) {
        // 校验成功后，才在本地执行升变移动
        const move = game.move({
          from: fromSquare,
          to: toSquare,
          promotion
        });
        
        if (move) {
          // 使用现有的成功处理逻辑
          playSound('move');
          
          // 更新棋盘状态
          setGameState(prev => prev ? {
            ...prev,
            fen: game.fen(),
            stats: {
              ...prev.stats,
              ...response.stats
            }
          } : prev);
          
          // 显示正确提示
          setOverlay({ visible: true, type: 'correct' });
          setTimeout(() => {
            setOverlay({ visible: false, type: null });
            
            if (response.isLastMove) {
              playSound('finish');
              setOverlay({ visible: true, type: 'complete' });
              if (typeof debouncedLoadStats === 'function') {
                debouncedLoadStats();
              }
            }
          }, 1000);
          
          // 处理对方的移动
          if (response.nextMove) {
            setTimeout(() => {
              try {
                // 确保棋盘状态正确
                if (response.nextFen) {
                  game.load(response.nextFen);
                }
                
                // 执行对手的移动
                const opponentMove = game.move({
                  from: response.nextMove.from,
                  to: response.nextMove.to,
                  promotion: response.nextMove.promotion || 'q'
                });
                
                setGameState(prev => {
                  if (!prev) return null;
                  return {
                    ...prev,
                    isPlayerTurn: true,
                    fen: game.fen()
                  };
                });
              } catch (moveError) {
                console.error('Error making opponent move:', moveError);
                // 如果出现错误，使用服务器返回的 FEN
                if (response.nextFen) {
                  game.load(response.nextFen);
                  setGameState(prev => ({
                    ...prev!,
                    isPlayerTurn: true,
                    fen: response.nextFen
                  }));
                }
              }
            }, 500);
          }
        } else {
          console.error('Failed to execute promotion move locally');
          message.error('执行升变移动失败');
        }
      } else {
        // 验证失败，恢复棋盘状态
        game.load(currentFen);
        setGameState(prev => ({
          ...prev!,
          fen: currentFen
        }));
        
        playSound('wrong');
        
        // 更新统计数据
        setGameState(prev => ({
          ...prev!,
          stats: {
            ...prev!.stats,
            ...response.stats
          }
        }));
        
        // 显示错误提示
        setOverlay({ visible: true, type: 'wrong' });
        
        setTimeout(() => {
          setOverlay({ visible: false, type: null });
        }, 1000);
      }
    } catch (error) {
      console.error('Error in validatePromotionMove:', error);
      // 处理错误，恢复棋盘状态
      game.load(currentFen);
      setGameState(prev => ({
        ...prev!,
        fen: currentFen
      }));
      
      if (error instanceof Error && error.message.includes('session')) {
        message.error('会话已过期，请重新登录');
        navigate('/login');
      } else {
        message.error('验证移动失败');
      }
    }
  };

  return (
    <MainLayout>
      <Content style={{ background: '#f5f5f5' }}>
        <Row gutter={24} wrap={isPortrait}>
          <Col flex={isPortrait ? '100%' : 'auto'} style={{ 
            transition: 'all 0.3s ease',
            maxWidth: isFullscreen ? '100%' : (isPortrait ? '100%' : `calc(100% - 350px)`)
          }}>
            <Card 
              className="chess-board-container" 
              bordered={false}
              bodyStyle={{ 
                padding: 0,
                height: '100%',
                background: 'transparent'
              }}
              style={{
                background: 'transparent'
              }}
            >
              <div className="chess-board-wrapper">
                <div className="board-container">
                  <div className="coordinate-wrapper" onContextMenu={handleContextMenu}>
                    <div className="board-coordinates rank-coordinates">
                      {boardOrientation === 'white' 
                        ? ['8', '7', '6', '5', '4', '3', '2', '1'].map((rank) => (
                          <div key={rank} className="coordinate-label">{rank}</div>
                        ))
                        : ['1', '2', '3', '4', '5', '6', '7', '8'].map((rank) => (
                          <div key={rank} className="coordinate-label">{rank}</div>
                        ))
                      }
                    </div>
                    <div className="board-coordinates file-coordinates">
                      {boardOrientation === 'white'
                        ? ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map((file) => (
                          <div key={file} className="coordinate-label">{file}</div>
                        ))
                        : ['h', 'g', 'f', 'e', 'd', 'c', 'b', 'a'].map((file) => (
                          <div key={file} className="coordinate-label">{file}</div>
                        ))
                      }
                    </div>
                    {gameState && (
                      <Chessboard
                        key={boardKey}
                        position={gameState?.fen}
                        boardOrientation={boardOrientation}
                        customArrows={customArrows}
                        onPieceDrop={onDrop}
                        onSquareClick={onSquareClick}
                        onPieceDragBegin={onPieceDragBegin}
                        onPieceDragEnd={onPieceDragEnd}
                        customSquareStyles={customSquareStyles}
                        customDarkSquareStyle={{ backgroundColor: currentTheme.dark }}
                        customLightSquareStyle={{ backgroundColor: currentTheme.light }}
                        showBoardNotation={false}
                        areArrowsAllowed={true}
                        onPromotionCheck={(sourceSquare, targetSquare, piece) => {
                          // console.log('=== onPromotionCheck called ===');
                          // console.log('Parameters:', { sourceSquare, targetSquare, piece });
                          
                          const sourcePiece = game.get(sourceSquare);
                          const isPromotion = sourcePiece?.type === 'p' && 
                            (targetSquare.charAt(1) === '8' || targetSquare.charAt(1) === '1');
                          
                          // ('Promotion check result:', { sourcePiece, isPromotion });
                          
                          // 不设置升变状态，让库不显示对话框
                          // 我们将在 onDrop 中手动处理升变
                          return false; // 总是返回 false，禁用库的升变对话框
                        }}
                        onPromotionPieceSelect={handlePromotionPieceSelect}
                        promotionToSquare={promotionTargetSquare}
                        showPromotionDialog={false}
                      />
                    )}
                    
                    {/* 自定义升变对话框 */}
                    {showPromotionDialogForced && promotionTargetSquare && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'white',
                        border: '2px solid #ccc',
                        borderRadius: '8px',
                        padding: '20px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <h3 style={{ margin: 0, color: '#333' }}>选择升变棋子</h3>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          {['Q', 'R', 'N', 'B'].map((pieceType) => {
                            const pieceColor = game.turn();
                            const pieceCode = `${pieceColor}${pieceType}`;
                            return (
                              <button
                                key={pieceType}
                                onClick={() => {
                                  handlePromotionPieceSelect(
                                    pieceCode as any,
                                    promotionSourceSquare || undefined,
                                    promotionTargetSquare
                                  );
                                }}
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  border: '2px solid #ddd',
                                  borderRadius: '8px',
                                  background: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '36px',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#1890ff';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#ddd';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                {pieceType === 'Q' ? '♕' : 
                                 pieceType === 'R' ? '♖' : 
                                 pieceType === 'N' ? '♘' : '♗'}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          onClick={() => {
                            setPromotionSourceSquare(null);
                            setPromotionTargetSquare(null);
                            setShowPromotionDialogForced(false);
                          }}
                          style={{
                            padding: '8px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#f5f5f5',
                            cursor: 'pointer'
                          }}
                        >
                          取消
                        </button>
                      </div>
                    )}
                  </div>
                  {overlay.visible && (
                    <Overlay
                      type={overlay.type}
                      onClose={() => setOverlay({ visible: false, type: null })}
                      onContinue={isCompleted ? handleRestart : handleNextLevel}
                      onReturn={handleReturn}
                      continueText={isCompleted ? "重置进度" : "下一关"}
                      isGameCompleted={isCompleted}
                    />
                  )}
                </div>
                {isFullscreen && (
                  <div className="fullscreen-controls">
                    <Button
                      icon={<RetweetOutlined />}
                      onClick={flipBoard}
                      type="text"
                    />
                    <Dropdown 
                      menu={{
                        items: boardThemes.map((theme) => ({
                          key: theme.name,
                          label: (
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              display: 'flex',
                              flexWrap: 'wrap',
                              background: '#fff',
                              padding: '2px'
                            }}>
                              <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
                              <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
                              <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
                              <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
                            </div>
                          ),
                          onClick: () => {
                            setCurrentTheme(theme);
                            setGameState(prev => ({
                              ...prev!,
                              fen: game.fen()
                            }));
                          }
                        }))
                      }}
                      placement="topLeft"
                      trigger={['click']}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                    >
                      <Button
                        type="text"
                        icon={
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            display: 'flex',
                            flexWrap: 'wrap',
                          }}>
                            <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
                            <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
                            <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
                            <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
                          </div>
                        }
                      />
                    </Dropdown>
                    <Button
                      icon={<InfoCircleOutlined />}
                      onClick={showHint}
                      type="text"
                    />
                    <Button
                      icon={<FullscreenExitOutlined />}
                      onClick={toggleFullscreen}
                      type="text"
                    />
                  </div>
                )}
              </div>
            </Card>
          </Col>
          
          {!isFullscreen && (
            <Col flex={isPortrait ? '100%' : '350px'} style={{ 
              transition: 'all 0.3s ease',
              minWidth: isPortrait ? '100%' : '350px',
              marginTop: isPortrait ? '12px' : 0
            }}>
              {isPortrait ? (
                <PortraitPanel
                  gameState={gameState}
                  stats={stats}
                  onFlipBoard={flipBoard}
                  onShowHint={showHint}
                  onThemeChange={(theme) => {
                    setCurrentTheme(theme);
                    setGameState(prev => ({
                      ...prev!,
                      fen: game.fen()
                    }));
                  }}
                  onToggleFullscreen={toggleFullscreen}
                  currentTheme={currentTheme}
                  activePopover={activePopover}
                  onPopoverVisibility={handlePopoverVisibility}
                  onShowStats={() => setStatsModalVisible(true)}
                />
              ) : (
                <LandscapePanel
                  gameState={gameState}
                  stats={stats}
                  statsLoading={statsLoading}
                  onFlipBoard={flipBoard}
                  onShowHint={showHint}
                  onThemeChange={(theme) => {
                    setCurrentTheme(theme);
                    setGameState(prev => ({
                      ...prev!,
                      fen: game.fen()
                    }));
                  }}
                  onToggleFullscreen={toggleFullscreen}
                  currentTheme={currentTheme}
                />
              )}
            </Col>
          )}

          {isFullscreen && (
            <div className="stats-panel-fullscreen">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                <Button
                  type="text"
                  icon={<RetweetOutlined style={{ fontSize: '16px' }} />}
                  onClick={flipBoard}
                  style={{ color: '#6b46c1', padding: '4px' }}
                />
                <Dropdown 
                  menu={{
                    items: boardThemes.map((theme) => ({
                      key: theme.name,
                      label: (
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexWrap: 'wrap',
                          background: '#fff',
                          padding: '2px'
                        }}>
                          <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
                          <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
                          <div style={{ width: '50%', height: '50%', backgroundColor: theme.dark }} />
                          <div style={{ width: '50%', height: '50%', backgroundColor: theme.light }} />
                        </div>
                      ),
                      onClick: () => {
                        setCurrentTheme(theme);
                        setGameState(prev => ({
                          ...prev!,
                          fen: game.fen()
                        }));
                      }
                    }))
                  }}
                  placement="topLeft"
                  trigger={['click']}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                >
                  <Button
                    type="text"
                    icon={
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexWrap: 'wrap',
                      }}>
                        <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
                        <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
                        <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.dark }} />
                        <div style={{ width: '50%', height: '50%', backgroundColor: currentTheme.light }} />
                      </div>
                    }
                  />
                </Dropdown>
                <Button
                  type="text"
                  icon={<InfoCircleOutlined style={{ fontSize: '16px' }} />}
                  onClick={showHint}
                  style={{ color: '#d97706', padding: '4px' }}
                />
                <Button
                  type="text"
                  icon={<FullscreenExitOutlined style={{ fontSize: '16px' }} />}
                  onClick={toggleFullscreen}
                  style={{ color: '#db2777', padding: '4px' }}
                />
              </div>
            </div>
          )}
        </Row>
      </Content>

      {/* 添加统计信息 Modal */}
      <Modal
        title={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '4px 0'
          }}>
            <TrophyOutlined style={{ 
              fontSize: '20px',
              color: '#059669' 
            }} />
            <span style={{
              fontSize: '16px',
              fontWeight: 500,
              color: '#1f1f1f'
            }}>{stats?.title || '未命名棋谱'}</span>
          </div>
        }
        open={statsModalVisible}
        onCancel={() => setStatsModalVisible(false)}
        footer={null}
        width={320}
      >
        <Divider style={{ margin: '0 0 16px 0' }} />
        
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: '16px', fontWeight: 500 }}>完成进度</span>
            <span style={{ float: 'right', fontSize: '16px', fontWeight: 500 }}>
              {gameState?.stats?.completed_variations || 0}/{gameState?.stats?.total_variations || 0}
            </span>
          </div>
          <Progress 
            percent={Math.round(((gameState?.stats?.completed_variations || 0) / (gameState?.stats?.total_variations || 1)) * 100)} 
            status="normal"
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
            trailColor="#f0f0f0"
            strokeWidth={20}
            showInfo={false}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: '16px', fontWeight: 500 }}>正确率</span>
            <span style={{ float: 'right', fontSize: '16px', fontWeight: 500 }}>
              {gameState?.stats?.accuracy || 0}%
            </span>
          </div>
          <Progress 
            percent={gameState?.stats?.accuracy || 0}
            status="normal"
            strokeColor={{
              '0%': '#ffa39e',
              '100%': '#52c41a',
            }}
            trailColor="#f0f0f0"
            strokeWidth={20}
            showInfo={false}
          />
        </div>

        <Divider style={{ margin: '24px 0' }} />

        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic
                title="步数"
                value={gameState?.stats?.total_moves || 0}
                prefix={<AimOutlined />}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="正确率"
                value={gameState?.stats?.accuracy || 0}
                suffix="%"
                valueStyle={{ color: '#108ee9' }}
              />
            </Col>
          </Row>
        </div>

        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic
                title="正确步数"
                value={gameState?.stats?.correct_moves || 0}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckOutlined />}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="错误步数"
                value={gameState?.stats?.wrong_moves || 0}
                valueStyle={{ color: '#cf1322' }}
                prefix={<CloseOutlined />}
              />
            </Col>
          </Row>
        </div>

        <div>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic
                title="最佳连走"
                value={gameState?.stats?.best_streak || 0}
                valueStyle={{ color: '#cf1322' }}
                prefix={<FireFilled />}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="当前连走"
                value={gameState?.stats?.current_streak || 0}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: (gameState?.stats?.current_streak || 0) > 0 ? '#3f8600' : '#595959' }}
              />
            </Col>
          </Row>
        </div>
      </Modal>
    </MainLayout>
  );
};

export default ChessBoard; 